import { Controller } from "@hotwired/stimulus"
import { post, patch, destroy } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import { showConfirmModal } from "../lib/custom_confirm"
import { syncGateReason } from "../lib/gate_reason"
import { SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"

// The reason a panel's Add submit gives while a blank name disables it
// (A11Y-1 / BUTTON-4). The view stamps the same text as
// data-admin-tags-disabled-reason so the controller re-applies exactly
// what was painted; this is the fallback for an unstamped host (keep in step).
export const DEFAULT_DISABLED_REASON = "Type a tag name"
// While the create request is in flight the submit is disabled for a different
// reason, and says so rather than going silent.
export const BUSY_REASON = "Adding the tag…"

// One controller for every admin tag panel (shared/_admin_tag_panel): System
// Tags and the Blog & KB Tags panels (#19716). A panel names its endpoint, the
// noun its toasts use and its delete question through Stimulus values; the
// defaults are System Tags' own, so that screen behaves exactly as before.
export default class extends Controller {
  static targets = ["nameInput", "submitButton", "tagsList", "emptyState", "badge", "count", "editInput", "saveButton", "cancelButton", "rowTemplate"]
  static values = {
    url: { type: String, default: "/admin/system_tags" },
    noun: { type: String, default: "System tag" },
    deleteConfirm: { type: String, default: "Delete system tag \"{name}\"? It will be removed from all users' leads and orders." }
  }

  connect() {
    this.#updateSubmitState()
  }

  // BUTTON-9: a row's rename Save follows its input — every edit input (server
  // rendered or appended after a create) re-checks the gate as it is typed in.
  editInputTargetConnected(input) {
    input.addEventListener("input", this.#onEditInput)
  }

  editInputTargetDisconnected(input) {
    input.removeEventListener("input", this.#onEditInput)
  }

  // Enable the panel's Add button only once the input has content (dirty).
  nameInputChanged() {
    this.#updateSubmitState()
  }

  async create(event) {
    event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    if (!name) return

    syncGateReason(this.submitButtonTarget, { ready: false, reason: BUSY_REASON })

    try {
      const response = await post(`${this.urlValue}.json`, {
        body: JSON.stringify({ tag: { name } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#appendTag(data.id, data.name)
        this.nameInputTarget.value = ""
        this.#hideEmptyState()
        if (data.promoted) {
          // showToast renders its message via insertAdjacentHTML — escape the tag name
          window.showToast(`Existing user tag "${escapeHtml(data.name)}" was promoted to a system tag`, "info")
        } else {
          window.showToast(`${escapeHtml(this.nounValue)} "${escapeHtml(data.name)}" created`)
        }
      } else {
        const error = await response.json
        window.showToast(error.error || `Failed to create ${this.#lowerNoun()}`, "danger")
      }
    } catch {
      window.showToast(`Failed to create ${this.#lowerNoun()}`, "danger")
    } finally {
      this.nameInputTarget.focus()
      this.#updateSubmitState()
    }
  }

  startEdit(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    const badge = item.querySelector("[data-admin-tags-target='badge']")
    const input = item.querySelector("[data-admin-tags-target='editInput']")
    const editBtn = event.currentTarget
    const saveBtn = item.querySelector("[data-admin-tags-target='saveButton']")
    const cancelBtn = item.querySelector("[data-admin-tags-target='cancelButton']")
    const deleteBtn = item.querySelector("[data-action='click->admin-tags#delete']")

    badge.classList.add("d-none")
    input.classList.remove("d-none")
    input.value = badge.textContent
    input.focus()
    input.select()

    editBtn.classList.add("d-none")
    deleteBtn.classList.add("d-none")
    saveBtn.classList.remove("d-none")
    cancelBtn.classList.remove("d-none")
    this.#syncRenameSave(item)
  }

  async saveEdit(event) {
    const saveBtn = event.currentTarget
    const item = saveBtn.closest(".tags-list-item")
    const tagId = item.dataset.tagId
    const input = item.querySelector("[data-admin-tags-target='editInput']")
    const newName = input.value.trim()

    if (!newName) return

    saveBtn.disabled = true
    saveBtn.setAttribute("aria-busy", "true")
    try {
      const response = await patch(`${this.urlValue}/${tagId}.json`, {
        body: JSON.stringify({ tag: { name: newName } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#exitEdit(item)
        // A rename onto a name the panel already lists merges the two (a
        // content tag set moves its taggings onto the existing tag): the
        // renamed row goes and the surviving row takes the new count.
        const survivor = this.#rowFor(data.id, item)
        if (survivor) {
          item.remove()
          this.#applyRowState(survivor, data)
        } else {
          const badge = item.querySelector("[data-admin-tags-target='badge']")
          badge.textContent = data.name
          item.dataset.tagId = data.id
          this.#updateActionLabels(item, data.name)
          this.#applyRowState(item, data)
        }
        window.showToast(`${escapeHtml(this.nounValue)} renamed to "${escapeHtml(data.name)}"`)
      } else {
        const error = await response.json
        window.showToast(error.error || `Failed to rename ${this.#lowerNoun()}`, "danger")
      }
    } catch {
      window.showToast(`Failed to rename ${this.#lowerNoun()}`, "danger")
    } finally {
      saveBtn.removeAttribute("aria-busy")
      // A landed rename closed the row: its hidden Save goes back to its own
      // enabled state (startEdit re-gates it). A failed one stays open, dirty.
      if (saveBtn.classList.contains("d-none")) {
        syncGateReason(saveBtn, { ready: true, reason: SAVE_UNCHANGED_REASON })
      } else {
        this.#syncRenameSave(item)
      }
    }
  }

  cancelEdit(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    this.#exitEdit(item)
  }

  editKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const item = event.currentTarget.closest(".tags-list-item")
      const saveBtn = item.querySelector("[data-admin-tags-target='saveButton']")
      this.#syncRenameSave(item)
      saveBtn.click()
    } else if (event.key === "Escape") {
      const item = event.currentTarget.closest(".tags-list-item")
      this.#exitEdit(item)
    }
  }

  async delete(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    const tagId = item.dataset.tagId
    const badge = item.querySelector("[data-admin-tags-target='badge']")
    const tagName = badge.textContent

    const confirmed = await showConfirmModal(this.deleteConfirmValue.replace("{name}", tagName))
    if (!confirmed) return

    try {
      const response = await destroy(`${this.urlValue}/${tagId}.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        item.remove()
        this.#showEmptyStateIfNeeded()
        window.showToast(`${escapeHtml(this.nounValue)} "${escapeHtml(tagName)}" deleted`)
      } else {
        const error = await response.json
        window.showToast(error.error || `Failed to delete ${this.#lowerNoun()}`, "danger")
      }
    } catch {
      window.showToast(`Failed to delete ${this.#lowerNoun()}`, "danger")
    }
  }

  // Private helpers

  #onEditInput = (event) => {
    const item = event.currentTarget.closest(".tags-list-item")
    if (item) this.#syncRenameSave(item)
  }

  // BUTTON-9: the rename Save is enabled only while the input holds a name that
  // differs from the one the row shows (the badge keeps the loaded name until a
  // rename lands), and says why while it is not. A blank name gives the Add
  // field's reason; the in-flight PATCH keeps it disabled until it settles.
  #syncRenameSave(item) {
    const saveBtn = item.querySelector("[data-admin-tags-target='saveButton']")
    const input = item.querySelector("[data-admin-tags-target='editInput']")
    const badge = item.querySelector("[data-admin-tags-target='badge']")
    if (!saveBtn || !input || !badge) return

    const name = input.value.trim()
    if (!name) {
      syncGateReason(saveBtn, { ready: false, reason: DEFAULT_DISABLED_REASON })
    } else {
      syncGateReason(saveBtn, { ready: name !== badge.textContent.trim(), reason: SAVE_UNCHANGED_REASON })
    }
  }

  // A11Y-1 / BUTTON-4: disabled while the name is blank AND explained — the
  // reason it was painted with as its gl-tooltip plus a "<label> — <reason>"
  // name; both withdrawn the moment a name enables it (lib/gate_reason).
  #updateSubmitState() {
    if (!this.hasSubmitButtonTarget || !this.hasNameInputTarget) return
    const ready = this.nameInputTarget.value.trim() !== ""
    syncGateReason(this.submitButtonTarget, { ready, reason: this.#disabledReason() })
  }

  #disabledReason() {
    return this.submitButtonTarget.dataset.adminTagsDisabledReason || DEFAULT_DISABLED_REASON
  }

  // A row created after a successful POST is a CLONE of the server-rendered
  // <template> (admin/system_tags/_tag_row) — the same partial that renders
  // every existing row — so the page never carries a second, hand-written copy
  // of the row anatomy that could drift from the HAML (#12564). Text lands via
  // textContent / value, never string interpolation, so the name needs no
  // escaping here; #updateActionLabels stamps every per-tag accessible name.
  #appendTag(id, name) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.dataset.tagId = String(id)
    row.querySelector("[data-admin-tags-target='badge']").textContent = name
    const input = row.querySelector("[data-admin-tags-target='editInput']")
    input.defaultValue = name
    input.value = name
    this.#updateActionLabels(row, name)
    this.tagsListTarget.appendChild(row)
  }

  // Per-tag accessible names go stale after a rename — refresh them in place.
  #updateActionLabels(item, name) {
    const labels = [
      ["[data-action='click->admin-tags#startEdit']", `Rename ${name}`],
      ["[data-action='click->admin-tags#saveEdit']", `Save ${name}`],
      ["[data-action='click->admin-tags#cancelEdit']", `Cancel renaming ${name}`],
      ["[data-action='click->admin-tags#delete']", `Delete ${name}`],
      [".gl-row-actions", `${name} actions`]
    ]

    labels.forEach(([selector, label]) => {
      const button = item.querySelector(selector)
      if (!button) return
      button.setAttribute("aria-label", label)
      // A gate-held button (the rename Save) restores its own name from here.
      if (button.hasAttribute("data-gl-gate-label")) button.setAttribute("data-gl-gate-label", label)
    })

    // A Delete held shut for a tag still in use keeps saying why under its new
    // name ("Delete x — Used on 3 blog posts …"), not just "Delete x".
    const deleteBtn = item.querySelector("[data-action='click->admin-tags#delete']")
    if (deleteBtn?.disabled) {
      syncGateReason(deleteBtn, { ready: false, reason: deleteBtn.getAttribute("data-gl-tooltip") })
    }

    const editInput = item.querySelector("[data-admin-tags-target='editInput']")
    if (editInput) editInput.setAttribute("aria-label", `New name for ${name}`)
  }

  // The other row already carrying `id` (a rename merged onto it), if any.
  #rowFor(id, except) {
    return Array.from(this.tagsListTarget.querySelectorAll(".tags-list-item"))
      .find((row) => row !== except && row.dataset.tagId === String(id))
  }

  // A content tag set answers with the row's use count and, while the tag is
  // in use, why Delete is unavailable (owner ruling 2026-10-28: a tag in use
  // cannot be deleted). System Tags answers with neither and is left alone.
  #applyRowState(row, data) {
    if (data.count_label !== undefined) {
      const count = row.querySelector("[data-admin-tags-target='count']")
      if (count) count.textContent = data.count_label
    }
    if (data.delete_reason !== undefined) {
      const deleteBtn = row.querySelector("[data-action='click->admin-tags#delete']")
      if (deleteBtn) syncGateReason(deleteBtn, { ready: !data.delete_reason, reason: data.delete_reason || "" })
    }
  }

  // "System tag" → "system tag", "Blog tag" → "blog tag"; an acronym
  // ("KB tag") keeps its capitals.
  #lowerNoun() {
    const noun = this.nounValue
    const acronym = noun.length > 1 && noun[1] === noun[1].toUpperCase() && noun[1] !== noun[1].toLowerCase()
    return acronym ? noun : noun.charAt(0).toLowerCase() + noun.slice(1)
  }

  #exitEdit(item) {
    const badge = item.querySelector("[data-admin-tags-target='badge']")
    const input = item.querySelector("[data-admin-tags-target='editInput']")
    const editBtn = item.querySelector("[data-action='click->admin-tags#startEdit']")
    const saveBtn = item.querySelector("[data-admin-tags-target='saveButton']")
    const cancelBtn = item.querySelector("[data-admin-tags-target='cancelButton']")
    const deleteBtn = item.querySelector("[data-action='click->admin-tags#delete']")

    badge.classList.remove("d-none")
    input.classList.add("d-none")
    editBtn.classList.remove("d-none")
    deleteBtn.classList.remove("d-none")
    saveBtn.classList.add("d-none")
    cancelBtn.classList.add("d-none")
  }

  #hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("d-none")
    }
  }

  #showEmptyStateIfNeeded() {
    const items = this.tagsListTarget.querySelectorAll(".tags-list-item")
    if (items.length === 0 && this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("d-none")
    }
  }
}
